import Router from 'next/router'
import uuid from 'uuid'

import { envConfigs } from '@/constants/config'
import { STORAGE_DATA } from '@/constants/storageData'
import { getVisitorId } from '@/methods/recommendTracking'

// 直播行為事件（與 shop-api dao/stats/live_session_track.go 的 Track* 一致）：
// 送到 POST /v1/room/track-events，後台「報表中心 → 直播場次報表」用來算封面點擊、商品點擊率、加購率、流量來源、未登入訪客數
export const LIVE_TRACK_EVENT = {
  ROOM_ENTER: 1, // 進房（帶流量來源）
  COVER_IMPRESSION: 2, // 直播封面曝光（帶封面所在位置）
  COVER_CLICK: 3, // 直播封面點擊
  PRODUCT_IMPRESSION: 4, // 直播間商品卡曝光
  PRODUCT_CLICK: 5, // 直播間商品點擊（含加入購物車、直接購買按鈕）
  ADD_TO_CART: 6, // 加入購物車成功
  BUY_NOW: 7, // 直接購買成功
  HEARTBEAT: 8, // 觀看心跳：直播間頁面在前景時每 60 秒一次（一次算 1 分鐘；下播後送的算回放）
  PLAY_START: 9, // 播放器第一次開始播放（每次進房一次）
  LIKE: 10, // 按讚（quantity = 次數）
  SHARE: 11, // 分享直播
  RESERVE: 12, // 點「預約直播」
} as const

export const LIVE_HEARTBEAT_INTERVAL = 60 * 1000

// 目前所在的直播間（/live/[roomId]、/live/auction/[roomId]，前面可能有語系），不在直播間時回空字串
export const currentLiveRoomId = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.pathname.match(/\/live\/(?:auction\/)?([A-Za-z0-9]{17,32})(?:\/|$)/)?.[1] || ''
}

export type LiveTrackEventType = (typeof LIVE_TRACK_EVENT)[keyof typeof LIVE_TRACK_EVENT]

// 直播間連結帶 ?src=來源，進房時記下來（站外連結沒有 src 時看 utm_source）
export const LIVE_SOURCE_QUERY = 'src'

export const LIVE_TRAFFIC_SOURCE = {
  HOME: 'home',
  LIVE_LIST: 'live_list',
  SEARCH: 'search',
  MERCHANT: 'merchant', // 商家／KOL 個人頁
  PRODUCT: 'product', // 商品頁的直播預告
  CALENDAR: 'calendar',
  MY_LIVE: 'my_live',
  FOLLOW: 'follow',
  LIVE_ROOM: 'live_room', // 從另一個直播間過來
  SHARE: 'share', // 分享出去的連結
  EXTERNAL: 'external', // 其他網站點進來、沒帶來源
  OTHER: 'other', // 站內其他頁面
} as const

// 依目前頁面判斷封面所在位置（goToLiveRoom、封面曝光共用）
export const liveSourceFromPathname = (pathname = ''): string => {
  if (pathname === '/' || pathname === '/explore') return LIVE_TRAFFIC_SOURCE.HOME
  if (pathname === '/live') return LIVE_TRAFFIC_SOURCE.LIVE_LIST
  if (pathname.startsWith('/live/')) return LIVE_TRAFFIC_SOURCE.LIVE_ROOM
  if (pathname.startsWith('/search')) return LIVE_TRAFFIC_SOURCE.SEARCH
  if (pathname.startsWith('/user') || pathname.startsWith('/profile')) return LIVE_TRAFFIC_SOURCE.MERCHANT
  if (pathname.startsWith('/product')) return LIVE_TRAFFIC_SOURCE.PRODUCT
  if (pathname.startsWith('/calendar')) return LIVE_TRAFFIC_SOURCE.CALENDAR
  if (pathname.startsWith('/my-live')) return LIVE_TRAFFIC_SOURCE.MY_LIVE
  if (pathname.startsWith('/follow')) return LIVE_TRAFFIC_SOURCE.FOLLOW
  return LIVE_TRAFFIC_SOURCE.OTHER
}

// 後端只收 a-z0-9_、最長 32 字
const normalizeSource = (value: unknown): string =>
  typeof value === 'string'
    ? value
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .slice(0, 32)
    : ''

// 這次進直播間的流量來源：?src= → ?utm_source= → 從其他網站點進來 → 未知（直接開網址、App 推播沒帶參數）
export const liveEnterSource = (query: Record<string, unknown>): string => {
  const src = normalizeSource(Array.isArray(query[LIVE_SOURCE_QUERY]) ? (query[LIVE_SOURCE_QUERY] as unknown[])[0] : query[LIVE_SOURCE_QUERY])
  if (src) return src
  const utm = normalizeSource(Array.isArray(query.utm_source) ? (query.utm_source as unknown[])[0] : query.utm_source)
  if (utm) return utm
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      if (new URL(document.referrer).origin !== window.location.origin) return LIVE_TRAFFIC_SOURCE.EXTERNAL
    } catch {
      return ''
    }
  }
  return ''
}

interface LiveTrackInput {
  type: LiveTrackEventType
  roomId?: string // 沒有場次時不送
  productId?: number
  quantity?: number
  source?: string
}

const FLUSH_INTERVAL = 5000
const FLUSH_SIZE = 20
const MAX_PER_REQUEST = 50 // 與後端 MaxTrackEventsPerRequest 一致

const queue: Record<string, unknown>[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let listening = false
// 同一頁面（一次路由）內同一張封面、同一個商品卡只記一次曝光；換頁就重算
let seenImpressions = new Set<string>()

const getToken = (): string => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_DATA.TOKEN) || 'null')?.token || ''
  } catch {
    return ''
  }
}

// 用 fetch keepalive：關頁、切背景時送出的那一批也不會被瀏覽器取消。追蹤失敗不影響使用者，錯誤直接忽略
const send = (events: Record<string, unknown>[]) => {
  const token = getToken()
  fetch(`${envConfigs.apiUrl}/v1/room/track-events`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    body: JSON.stringify({ visitor_id: getVisitorId(), events }),
  }).catch(() => undefined)
}

export const flushLiveEvents = () => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  while (queue.length) {
    send(queue.splice(0, MAX_PER_REQUEST))
  }
}

const listen = () => {
  if (listening || typeof window === 'undefined') return
  listening = true
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && flushLiveEvents())
  window.addEventListener('pagehide', flushLiveEvents)
  Router.events.on('routeChangeComplete', () => {
    seenImpressions = new Set()
  })
}

export const trackLiveEvent = ({ type, roomId, productId, quantity, source }: LiveTrackInput) => {
  if (typeof window === 'undefined' || !roomId) return
  listen()
  queue.push({
    event_id: uuid.v4(),
    room_id: roomId,
    type,
    ...(productId && { product_id: productId }),
    ...(quantity && { quantity }),
    ...(source && { source }),
    client_at: Date.now(),
  })
  if (queue.length >= FLUSH_SIZE) {
    flushLiveEvents()
  } else if (!timer) {
    timer = setTimeout(flushLiveEvents, FLUSH_INTERVAL)
  }
}

// 曝光：同一頁面內同一個目標只記一次
export const trackLiveImpression = (input: LiveTrackInput) => {
  const key = `${input.type}:${input.roomId}:${input.productId || 0}`
  if (seenImpressions.has(key)) return
  seenImpressions.add(key)
  trackLiveEvent(input)
}

// 播放器開始播放：只在直播間頁面記（列表頁的預覽播放器不算），每次進房只記一次
export const trackLivePlayStart = () => {
  const roomId = currentLiveRoomId()
  if (!roomId) return
  trackLiveImpression({ type: LIVE_TRACK_EVENT.PLAY_START, roomId })
}

// 在直播間的頁面才記（例如分享彈窗是共用元件）
export const trackCurrentLiveRoomEvent = (type: LiveTrackEventType, quantity?: number) => {
  const roomId = currentLiveRoomId()
  if (!roomId) return
  trackLiveEvent({ type, roomId, quantity })
}
